import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Mail, CheckCircle, ArrowLeft, Lock, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';

type AuthMode = 'password' | 'magic-link';

export function LoginPage() {
  const { t, i18n } = useTranslation('auth');
  const { locale } = useParams();
  const currentLocale = locale || i18n.language || 'fr';
  const navigate = useNavigate();
  const { user, signIn, signInWithPassword } = useAuth();

  const [mode, setMode] = useState<AuthMode>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [error, setError] = useState('');

  // Si déjà connecté, rediriger vers le profil
  if (user) {
    navigate(`/${currentLocale}/profile`);
    return null;
  }

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await signInWithPassword(email, password);
      navigate(`/${currentLocale}/profile`);
    } catch (err: any) {
      setError(err.message || t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  const handleMagicLinkSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await signIn(email);
      setMagicLinkSent(true);
    } catch (err: any) {
      setError(err.message || t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  // Écran de confirmation magic link
  if (magicLinkSent) {
    return (
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-md mx-auto">
          <Card className="p-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-aspexit-green rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-white" />
              </div>
              <h1 className="text-2xl font-heading font-extrabold text-aspexit-black mb-4">
                {t('magicLink.sent.title')}
              </h1>
              <p className="text-aspexit-gray mb-6">
                {t('magicLink.sent.description', { email })}
              </p>
              <p className="text-sm text-aspexit-gray">
                {t('magicLink.sent.checkSpam')}
              </p>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-16">
      <div className="max-w-md mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate(`/${currentLocale}`)}
          className="mb-6 -ml-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('backToHome')}
        </Button>

        <Card className="p-8">
          <div className="mb-6">
            <h1 className="text-3xl font-heading font-extrabold text-aspexit-black mb-2">
              {t('login.title')}
            </h1>
            <p className="text-aspexit-gray">
              {t('login.subtitle')}
            </p>
          </div>

          {/* Onglets de mode */}
          <div className="flex rounded-lg bg-gray-100 p-1 mb-6">
            <button
              type="button"
              onClick={() => { setMode('password'); setError(''); }}
              className={cn(
                'flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-all',
                mode === 'password'
                  ? 'bg-white text-aspexit-black shadow-sm'
                  : 'text-aspexit-gray hover:text-aspexit-black'
              )}
            >
              <Lock className="w-4 h-4" />
              {t('login.tabs.password')}
            </button>
            <button
              type="button"
              onClick={() => { setMode('magic-link'); setError(''); }}
              className={cn(
                'flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-all whitespace-nowrap',
                mode === 'magic-link'
                  ? 'bg-white text-aspexit-black shadow-sm'
                  : 'text-aspexit-gray hover:text-aspexit-black'
              )}
            >
              <Sparkles className="w-4 h-4" />
              {t('login.tabs.magicLink')}
            </button>
          </div>

          {/* Formulaire email/mot de passe */}
          {mode === 'password' && (
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email" className="mb-2 block">{t('login.emailLabel')}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-aspexit-gray" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('login.emailPlaceholder')}
                    required
                    className="pl-10"
                    disabled={loading}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="password" className="mb-2 block">{t('login.passwordLabel')}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-aspexit-gray" />
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('login.passwordPlaceholder')}
                    required
                    className="pl-10"
                    disabled={loading}
                  />
                </div>
              </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              <Button
                type="submit"
                className="w-full"
                disabled={loading}
                size="lg"
              >
                {loading ? t('login.signingIn') : t('login.submitPassword')}
              </Button>
            </form>
          )}

          {/* Formulaire magic link */}
          {mode === 'magic-link' && (
            <>
              <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
                <h3 className="font-semibold text-aspexit-black mb-1 flex items-center gap-2 text-sm">
                  <Sparkles className="w-4 h-4 text-blue-600" />
                  {t('magicLink.info.title')}
                </h3>
                <p className="text-sm text-aspexit-gray">
                  {t('magicLink.info.description')}
                </p>
              </div>

              <form onSubmit={handleMagicLinkSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="email-magic" className="mb-2 block">{t('login.emailLabel')}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-aspexit-gray" />
                    <Input
                      id="email-magic"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t('login.emailPlaceholder')}
                      required
                      className="pl-10"
                      disabled={loading}
                    />
                  </div>
                </div>

                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full"
                  disabled={loading}
                  size="lg"
                >
                  {loading ? t('login.sending') : t('login.submitMagicLink')}
                </Button>
              </form>
            </>
          )}

          <div className="mt-6 text-center text-sm text-aspexit-gray">
            {t('login.needHelp')}{' '}
            <Link to={`/${currentLocale}/contact`} className="text-aspexit-green hover:underline font-medium">
              {t('login.contactUs')}
            </Link>
          </div>
        </Card>
      </div>
    </div>
  );
}
